import { useState } from 'react';
import { toast } from 'react-toastify';
import Api from '../../../util/api.js';
import { partial } from '../../../util/translation/translation';

const s = partial('shared');

export const useUserLoginData = () => {
  const [loginData, setLoginData] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [totalSessions, setTotalSessions] = useState(0);
  const [sessionsPerUser, setSessionsPerUser] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [deviceData, setDeviceData] = useState([]);
  const [screenData, setScreenData] = useState([]);
  const [osByDateData, setOsByDateData] = useState([]);
  const [newVsReturningData, setNewVsReturningData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const processData = (response, type) => {
    try {
      if (!response) return;

      let transformedData = [];

      if (response.byDate && response.byDate.length > 0) {
        const sortedData = [...response.byDate].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        if (type === 'all') {
          transformedData = sortedData.map((item) => ({
            date: item.date,
            allUsers: item.allUserCount,
          }));
        } else if (type === 'unique') {
          transformedData = sortedData.map((item) => ({
            date: item.date,
            uniqueUsers: item.uniqueUserCount,
          }));
        } else {
          transformedData = sortedData.map((item) => ({
            date: item.date,
            allUsers: item.allUserCount,
            uniqueUsers: item.uniqueUserCount,
          }));
        }
      }

      setLoginData(transformedData || []);
      setTotalSessions(response?.totalSessionsForAllUsers || 0);
      setSessionsPerUser(
        typeof response?.averageSessionPerUser === 'number'
          ? parseFloat(response.averageSessionPerUser.toFixed(2))
          : 0
      );

      const totalUnique =
        response?.byDate?.reduce((max, item) => {
          return Math.max(max, item.uniqueUserCount || 0);
        }, 0) || 0;

      setActiveUsers(totalUnique);
      setDeviceData(response?.byDevice || []);

      const byDateAndDevice = response?.byDateAndDevice || [];
      const pivotedByDate = {};
      byDateAndDevice.forEach(({ date, os, uniqueUserCount }) => {
        if (!pivotedByDate[date]) pivotedByDate[date] = { date };
        pivotedByDate[date][os] = uniqueUserCount;
      });
      setOsByDateData(
        Object.values(pivotedByDate).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        )
      );

      setNewVsReturningData(
        [...(response?.newVsReturningByDate || [])].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        )
      );

      setHourlyData(
        [...(response?.byHour || [])].sort(
          (a, b) => new Date(a.hour) - new Date(b.hour)
        )
      );
    } catch (e) {
      console.error('Process data error:', e);
    }
  };

  const fetchUserLoginStats = async (timeframeParam, granularity) => {
    try {
      setIsLoading(true);
      const data = await Api.analytics.getAllFirebaseUserLoginsByDate(
        timeframeParam,
        granularity
      );
      const response = data.data;

      setRawData(response);
      processData(response, 'all');

      const filteredScreenData =
        response?.screenViews
          ?.filter((sv) => sv.screenName !== null)
          .sort((a, b) => b.viewCount - a.viewCount) || [];

      setScreenData(filteredScreenData);
      setIsLoading(false);

      return response;
    } catch (error) {
      setIsLoading(false);
      toast.error(error || s('somethingWentWrong'));
      setLoginData([]);
      setTotalSessions(0);
      setSessionsPerUser(0);
      setScreenData([]);
      setDeviceData([]);
      setOsByDateData([]);
      setNewVsReturningData([]);
      setHourlyData([]);
      return null;
    }
  };

  const fetchCustomDateStats = async (startDate, endDate, granularity) => {
    try {
      setIsLoading(true);
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];

      const data = await Api.analytics.getAllFirebaseUserLoginsByCustomDate(
        'custom',
        formattedStartDate,
        formattedEndDate,
        granularity
      );

      const response = data.data;
      setRawData(response);

      processData(response, 'all');

      const filteredScreenData =
        response?.screenViews
          ?.filter((sv) => sv.screenName !== null)
          .sort((a, b) => a.viewCount - b.viewCount) || [];

      setScreenData(filteredScreenData);
      setIsLoading(false);

      return response;
    } catch (error) {
      setIsLoading(false);
      toast.error(error || s('somethingWentWrong'));
      setLoginData([]);
      setTotalSessions(0);
      setSessionsPerUser(0);
      setScreenData([]);
      setDeviceData([]);
      setOsByDateData([]);
      setNewVsReturningData([]);
      setHourlyData([]);
      return null;
    }
  };

  return {
    loginData,
    rawData,
    totalSessions,
    sessionsPerUser,
    activeUsers,
    deviceData,
    screenData,
    osByDateData,
    newVsReturningData,
    hourlyData,
    isLoading,
    fetchUserLoginStats,
    fetchCustomDateStats,
    processData,
  };
};
